import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Alert, CircularProgress, Avatar, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  List, ListItem, ListItemText, Menu, MenuItem, ListItemIcon, Checkbox,
} from '@mui/material';
import {
  Add as AddIcon, Close as CloseIcon, OpenInNew as OpenInNewIcon, Search as SearchIcon,
  Business as BusinessIcon, HourglassEmpty as PendingIcon, VpnKey as KeyIcon,
  CheckCircle as VerifiedIcon, MoreVert as MoreIcon, LinkOff as DisconnectIcon,
} from '@mui/icons-material';
import ApiService from '../../../services/api.service';
import { err, statusColor, useConnectionCap, ConnectionsHeader } from './shared';
import { can } from '../../../utils/roleUtils';

const rowCard = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
  p: 1.25, mb: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
  bgcolor: 'background.paper', transition: 'box-shadow .15s ease', '&:last-of-type': { mb: 0 },
  '&:hover': { boxShadow: 1 },
};
const sectionPaper = { p: 2, mb: 2, borderRadius: 2 };

const ProvidersPage = ({ currentUser }) => {
  const canWrite = can(currentUser, 'connections', 'write');
  const { cap, reloadCap } = useConnectionCap(false);

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tcContact, setTcContact] = useState('');

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuConn, setMenuConn] = useState(null);

  // Add Partner dialog
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Request Quote (inquiry) dialog
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryTarget, setInquiryTarget] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ services: '', contactNumber: '', contactName: '', notes: '' });
  const [sendingInquiry, setSendingInquiry] = useState(false);

  // Portal credential dialog
  const [credOpen, setCredOpen] = useState(false);
  const [credConn, setCredConn] = useState(null);
  const [credStatus, setCredStatus] = useState({ configured: false, username: '', verified_at: null });
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [credLoading, setCredLoading] = useState(false);
  const [credSaving, setCredSaving] = useState(false);
  const [credVerifying, setCredVerifying] = useState(false);

  // On-behalf allowed-orgs dialog (beta feature — removable). Lets this (client) TC choose which of
  // its organizations the CRP provider may create work orders for on its behalf.
  const [allowOpen, setAllowOpen] = useState(false);
  const [allowOrgs, setAllowOrgs] = useState([]);
  const [allowSelected, setAllowSelected] = useState(new Set());
  const [allowLoading, setAllowLoading] = useState(false);
  const [allowSaving, setAllowSaving] = useState(false);

  const openAllow = async () => {
    setAllowOpen(true);
    setAllowLoading(true);
    try {
      const res = await ApiService.getOnBehalfAllowedOrgs();
      const orgs = res?.data?.data?.organizations || [];
      setAllowOrgs(orgs);
      setAllowSelected(new Set(orgs.filter((o) => o.allowed).map((o) => o.id)));
    } catch (e) {
      setError(err(e, 'Failed to load organizations'));
      setAllowOpen(false);
    } finally {
      setAllowLoading(false);
    }
  };
  const toggleAllow = (id) => {
    setAllowSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const saveAllow = async () => {
    setAllowSaving(true);
    try {
      await ApiService.updateOnBehalfAllowedOrgs({ organization_ids: Array.from(allowSelected) });
      setSuccess('On-behalf organizations updated');
      setAllowOpen(false);
    } catch (e) {
      setError(err(e, 'Failed to save on-behalf organizations'));
    } finally {
      setAllowSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const conns = await ApiService.getTenantConnections();
      setConnections(conns?.data?.data || []);
      try {
        const cc = await ApiService.getCompanyConfig();
        const d = cc?.data?.data || {};
        setTcContact(d.mobile || d.telephone || '');
      } catch (e) { /* no company config yet */ }
      await reloadCap();
    } catch (e) {
      setError(err(e, 'Failed to load providers'));
    } finally {
      setLoading(false);
    }
  }, [reloadCap]);

  useEffect(() => { load(); }, [load]);

  const openMenu = (e, c) => { setMenuAnchor(e.currentTarget); setMenuConn(c); };
  const closeMenu = () => { setMenuAnchor(null); setMenuConn(null); };

  const openB2bPortal = async (connectionId) => {
    try {
      const res = await ApiService.initiatePortalAutoLogin(connectionId);
      const data = res?.data?.data;
      if (!data?.subdomain || !data?.token) throw new Error('Could not start the provider portal session');
      const parts = window.location.host.split('.');
      parts[0] = data.subdomain;
      window.open(`${window.location.protocol}//${parts.join('.')}/portal/auto-login?token=${encodeURIComponent(data.token)}`, '_blank', 'noopener');
    } catch (e) { setError(err(e, 'Failed to open provider portal')); }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this provider? In-flight work orders continue but no new handoffs.')) return;
    try { await ApiService.disconnectTenantConnection(id, ''); setSuccess('Disconnected'); load(); }
    catch (e) { setError(err(e, 'Failed to disconnect')); }
  };

  const cancel = async (id) => {
    try { await ApiService.cancelTenantConnection(id); setSuccess('Request cancelled'); load(); }
    catch (e) { setError(err(e, 'Failed to cancel')); }
  };

  const runSearch = async () => {
    setSearching(true);
    try { const res = await ApiService.searchB2bs(query); setResults(res?.data?.data || []); }
    catch (e) { setResults([]); setError(err(e, 'Search failed')); }
    finally { setSearching(false); }
  };

  const connect = async (b2bId) => {
    try { await ApiService.requestTenantConnection(b2bId, ''); setSuccess('Connection request sent'); setAddOpen(false); load(); }
    catch (e) { setError(err(e, 'Failed to send request')); }
  };

  const openInquiry = (b) => {
    setInquiryTarget({ id: b.id, name: b.name, code: b.code });
    setInquiryForm({ services: '', contactNumber: tcContact || '', contactName: '', notes: '' });
    setInquiryOpen(true);
  };

  const submitInquiry = async () => {
    const services = inquiryForm.services.split(',').map((s) => s.trim()).filter(Boolean);
    if (services.length === 0) { setError('List at least one service you need.'); return; }
    if (!inquiryForm.contactNumber.trim()) { setError('A contact number is required.'); return; }
    setSendingInquiry(true);
    try {
      await ApiService.sendB2bInquiry({
        target_typing_center_id: inquiryTarget.id, services,
        contact_number: inquiryForm.contactNumber.trim(),
        contact_name: inquiryForm.contactName.trim() || null,
        notes: inquiryForm.notes.trim() || null,
      });
      setSuccess('Inquiry sent — the provider will contact you directly.');
      setInquiryOpen(false);
    } catch (e) { setError(err(e, 'Failed to send inquiry')); }
    finally { setSendingInquiry(false); }
  };

  const openCred = async (c) => {
    setCredConn({ id: c.id, name: c.provider_name });
    setCredForm({ username: '', password: '' });
    setCredStatus({ configured: false, username: '', verified_at: null });
    setCredOpen(true);
    setCredLoading(true);
    try {
      const res = await ApiService.getPortalCredential(c.id);
      const d = res?.data?.data || {};
      setCredStatus({ configured: !!d.configured, username: d.username || '', verified_at: d.verified_at || null });
      setCredForm({ username: d.username || '', password: '' });
    } catch (e) { setError(err(e, 'Failed to load credential')); }
    finally { setCredLoading(false); }
  };

  const saveCred = async () => {
    if (!credForm.username.trim() || !credForm.password) { setError('Portal username/email and password are required.'); return; }
    setCredSaving(true);
    try {
      const res = await ApiService.savePortalCredential(credConn.id, credForm.username.trim(), credForm.password);
      const d = res?.data?.data || {};
      setCredStatus({ configured: true, username: d.username || credForm.username.trim(), verified_at: d.verified_at || null });
      setCredForm((f) => ({ ...f, password: '' }));
      setSuccess('Portal credential saved and verified');
    } catch (e) { setError(err(e, 'Failed to save credential')); }
    finally { setCredSaving(false); }
  };

  const verifyCred = async () => {
    setCredVerifying(true);
    try {
      const res = await ApiService.verifyPortalCredential(credConn.id);
      const d = res?.data?.data || {};
      setCredStatus((s) => ({ ...s, configured: true, verified_at: d.verified_at || null }));
      setSuccess('Portal credential verified');
    } catch (e) { setError(err(e, 'Verification failed')); }
    finally { setCredVerifying(false); }
  };

  const removeCred = async () => {
    if (!window.confirm('Remove your saved portal credential for this provider?')) return;
    try {
      await ApiService.deletePortalCredential(credConn.id);
      setCredStatus({ configured: false, username: '', verified_at: null });
      setCredForm({ username: '', password: '' });
      setSuccess('Portal credential removed');
    } catch (e) { setError(err(e, 'Failed to remove credential')); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const activePartners = connections.filter((c) => c.status === 'APPROVED');
  const myPendingRequests = connections.filter((c) => c.status === 'PENDING' && c.initiated_by === 'CLIENT');

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <ConnectionsHeader
        icon={<BusinessIcon />}
        title="Providers"
        cap={cap}
        action={canWrite ? (
          <Button variant="contained" startIcon={<AddIcon />} disabled={cap?.reached}
            onClick={() => { setResults([]); setQuery(''); setAddOpen(true); }} sx={{ textTransform: 'none' }}>
            Add Provider
          </Button>
        ) : null}
      />

      {cap?.reached && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You've reached your plan's provider limit ({cap.used}/{cap.limit}). Upgrade your plan to add more.
        </Alert>
      )}

      {myPendingRequests.length > 0 && (
        <Paper sx={sectionPaper}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Pending Requests</Typography>
          {myPendingRequests.map((c) => (
            <Box key={c.id} sx={{ ...rowCard, borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.light' }}><PendingIcon sx={{ fontSize: 18 }} /></Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.provider_name} ({c.provider_code})</Typography>
              </Box>
              {can(currentUser, 'connections', 'delete') && (
                <Button size="small" color="inherit" onClick={() => cancel(c.id)} sx={{ textTransform: 'none' }}>Cancel</Button>
              )}
            </Box>
          ))}
        </Paper>
      )}

      <Paper sx={sectionPaper}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Active Providers</Typography>
        {activePartners.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No providers yet.{canWrite && <> Use <b>Add Provider</b> to connect.</>}</Typography>
        ) : activePartners.map((c, idx) => (
          <Box key={c.id} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.25,
            borderBottom: idx < activePartners.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <BusinessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} noWrap>{c.provider_name}</Typography>
              <Typography variant="caption" color="text.secondary">{c.provider_code}</Typography>
              {c.is_promoted && <Chip label="Origin" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />}
            </Box>
            <IconButton size="small" onClick={(e) => openMenu(e, c)}><MoreIcon /></IconButton>
          </Box>
        ))}
      </Paper>

      {/* Per-provider action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { const c = menuConn; closeMenu(); openB2bPortal(c.id); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>Open portal
        </MenuItem>
        <MenuItem onClick={() => { const c = menuConn; closeMenu(); openCred(c); }}>
          <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>Credentials
        </MenuItem>
        {menuConn && menuConn.provider_code === 'CRP' && (
          <MenuItem onClick={() => { closeMenu(); openAllow(); }}>
            <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>On-behalf orgs
          </MenuItem>
        )}
        {menuConn && !menuConn.is_promoted && canWrite && (
          <MenuItem onClick={() => { const c = menuConn; closeMenu(); disconnect(c.id); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DisconnectIcon fontSize="small" color="error" /></ListItemIcon>Disconnect
          </MenuItem>
        )}
      </Menu>

      {/* On-behalf allowed-orgs dialog (beta — removable). Shown from the CRP provider row only. */}
      <Dialog open={allowOpen} onClose={() => setAllowOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">On-behalf organizations</Typography>
          <IconButton size="small" onClick={() => setAllowOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 1 }}>
            Choose which of your organizations this provider may create work orders for on your behalf.
            Walk-in orders are always allowed. If none are selected, the provider can only create walk-in orders.
          </Typography>
          {allowLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : allowOrgs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">You have no organizations yet.</Typography>
          ) : (
            <List dense>
              {allowOrgs.map((o) => (
                <ListItem key={o.id} disableGutters
                  secondaryAction={<Checkbox edge="end" checked={allowSelected.has(o.id)} onChange={() => toggleAllow(o.id)} />}>
                  <ListItemText primary={o.name} secondary={o.type} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllowOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveAllow} disabled={allowSaving || allowLoading} sx={{ textTransform: 'none' }}>
            {allowSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Provider dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Find a Provider</Typography>
          <IconButton size="small" onClick={() => setAddOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, pt: 1 }}>
            <TextField fullWidth size="small" placeholder="Search by name or exact code"
              value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch} sx={{ textTransform: 'none' }}>Search</Button>
          </Box>
          {searching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : results.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No results. Search by a provider's name or exact code.</Typography>
          ) : (
            <List dense>
              {results.map((b) => (
                <ListItem key={b.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                  secondaryAction={
                    b.existing_connection_status
                      ? <Chip label={b.existing_connection_status} size="small" color={statusColor(b.existing_connection_status)} />
                      : (
                        <Stack direction="row" spacing={1}>
                          {canWrite && (
                            <>
                              <Button size="small" variant="outlined" onClick={() => openInquiry(b)} sx={{ textTransform: 'none' }}>Request Quote</Button>
                              <Button size="small" variant="contained" onClick={() => connect(b.id)} sx={{ textTransform: 'none' }}>Connect</Button>
                            </>
                          )}
                        </Stack>
                      )
                  }>
                  <ListItemText
                    primary={`${b.name} (${b.code})`}
                    secondary={[b.tagline, (b.specializations || []).join(', '), (b.emirates || []).join(', ')].filter(Boolean).join(' · ')}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
      </Dialog>

      {/* Request Quote dialog */}
      <Dialog open={inquiryOpen} onClose={() => setInquiryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" noWrap>Request a Quote{inquiryTarget ? ` — ${inquiryTarget.name}` : ''}</Typography>
          <IconButton size="small" onClick={() => setInquiryOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              List the services you need and a contact number. The provider will reach out to you directly to discuss pricing — no pricing is exchanged in the app.
            </Typography>
            <TextField fullWidth size="small" label="Services needed (comma-separated)" sx={{ mb: 2 }} placeholder="Visa Renewal, Medical Typing"
              value={inquiryForm.services} onChange={(e) => setInquiryForm((f) => ({ ...f, services: e.target.value }))} />
            <TextField fullWidth size="small" label="Contact number" sx={{ mb: 2 }}
              value={inquiryForm.contactNumber} onChange={(e) => setInquiryForm((f) => ({ ...f, contactNumber: e.target.value }))} />
            <TextField fullWidth size="small" label="Contact name (optional)" sx={{ mb: 2 }}
              value={inquiryForm.contactName} onChange={(e) => setInquiryForm((f) => ({ ...f, contactName: e.target.value }))} />
            <TextField fullWidth size="small" label="Notes (optional)" multiline rows={2}
              value={inquiryForm.notes} onChange={(e) => setInquiryForm((f) => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInquiryOpen(false)} disabled={sendingInquiry} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={submitInquiry} disabled={sendingInquiry} sx={{ textTransform: 'none' }}>
            {sendingInquiry ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Portal credential dialog */}
      <Dialog open={credOpen} onClose={() => setCredOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" noWrap>Portal Credential{credConn ? ` — ${credConn.name}` : ''}</Typography>
          <IconButton size="small" onClick={() => setCredOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {credLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your own login for this provider's portal, used to auto-login. Only you can see and use it.
              </Typography>
              <Box sx={{ mb: 2 }}>
                {credStatus.configured ? (
                  credStatus.verified_at
                    ? <Chip size="small" color="success" icon={<VerifiedIcon />} label={`Verified ${new Date(credStatus.verified_at).toLocaleString()}`} />
                    : <Chip size="small" color="warning" label="Saved — not verified" />
                ) : <Chip size="small" variant="outlined" label="Not set up yet" />}
              </Box>
              <TextField fullWidth size="small" label="Portal username or email" sx={{ mb: 2 }}
                value={credForm.username} onChange={(e) => setCredForm((f) => ({ ...f, username: e.target.value }))} />
              <TextField fullWidth size="small" type="password" label="Portal password" autoComplete="new-password"
                placeholder={credStatus.configured ? 'Enter to update / re-verify' : ''}
                value={credForm.password} onChange={(e) => setCredForm((f) => ({ ...f, password: e.target.value }))} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
          <Box>{credStatus.configured && <Button size="small" color="error" onClick={removeCred} sx={{ textTransform: 'none' }}>Remove</Button>}</Box>
          <Stack direction="row" spacing={1}>
            {credStatus.configured && (
              <Button variant="outlined" color="secondary" onClick={verifyCred} disabled={credVerifying || credSaving} sx={{ textTransform: 'none' }}>
                {credVerifying ? 'Verifying...' : 'Verify'}
              </Button>
            )}
            <Button variant="contained" onClick={saveCred} disabled={credSaving || credVerifying} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {credSaving ? 'Saving...' : (credStatus.configured ? 'Update' : 'Save')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProvidersPage;
